import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, Mail, MessageCircle, Printer, PackageSearch } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useNavigate } from "react-router-dom";
import { companyCurrencies } from "@/lib/currencies";

export default function PartyPage({ kind }) {
  const { currentCompany } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const ti = (key, vars = {}) => Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, v), t(key));
  const cur = currentCompany?.base_currency || "SYP";
  const CURRENCIES = companyCurrencies(currentCompany);
  const endpoint = kind === "customer" ? "customers" : "suppliers";
  const title = kind === "customer" ? t("customers") : t("suppliers");
  const partyWord = kind === "customer" ? t("party_customer") : t("party_supplier");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name:"", phone:"", address:"", opening_balance:0, currency: cur, opening_balances:{}, notes:"" });
  const [stmt, setStmt] = useState(null);
  const [showProducts, setShowProducts] = useState(false);

  const load = async () => { try { const { data } = await api.get(`/${endpoint}`); setItems(data); } catch(e){ toast.error(formatErr(e)); } };
  useEffect(() => { load(); }, [kind]); // eslint-disable-line

  const openNew = () => { setEditing(null); setForm({ name:"", phone:"", address:"", opening_balance:0, currency: cur, opening_balances:{}, notes:"" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({...p}); setOpen(true); };
  const save = async () => {
    try {
      const body = {...form, opening_balance: +form.opening_balance};
      if (editing) await api.put(`/${endpoint}/${editing.id}`, body);
      else await api.post(`/${endpoint}`, body);
      toast.success(t("saved")); setOpen(false); load();
    } catch(e){ toast.error(formatErr(e)); }
  };
  const del = async (id) => {
    if (!confirm(t("confirm_delete"))) return;
    try { await api.delete(`/${endpoint}/${id}`); toast.success(t("deleted")); load(); } catch(e){ toast.error(formatErr(e)); }
  };
  const showStatement = async (p) => {
    try {
      const { data } = await api.get(`/reports/party-statement/${p.id}`, { params: { kind } });
      setStmt(data); setShowProducts(false);
    } catch(e){ toast.error(formatErr(e)); }
  };

  const emailStatement = async (p) => {
    const to = window.prompt(ti("party_email_prompt", { name: p.name }), p.email || "");
    if (!to || !to.trim()) return;
    try {
      const { data } = await api.post(`/reports/party-statement/${p.id}/email?kind=${kind}`, { to_email: to.trim() });
      toast.success(data?.attached_pdf ? ti("party_email_sent_pdf", { to }) : ti("party_email_sent", { to }));
    } catch (e) { toast.error(formatErr(e)); }
  };

  const whatsappStatement = async (p) => {
    try {
      const { data } = await api.get(`/reports/party-statement/${p.id}/whatsapp-text`, { params: { kind } });
      let phone = data.phone || "";
      if (!phone) {
        phone = window.prompt(ti("party_wa_prompt", { name: p.name }), "");
        if (!phone) return;
        phone = phone.replace(/\D+/g, "");
      }
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(data.text)}`;
      window.open(url, "_blank");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const goToInvoice = (i) => {
    setStmt(null);
    const k = i.kind || "sale";
    if (k === "purchase" || k === "purchase_return") nav(`/purchases?focus=${encodeURIComponent(i.number)}`);
    else nav(`/invoices?focus=${encodeURIComponent(i.number)}&kind=${k}`);
  };

  const printStatement = () => {
    if (!stmt) return;
    const p = stmt.party || {};
    const company = currentCompany?.name || "";
    const money = (n, c) => `${Number(n || 0).toLocaleString("ar-EG")} ${c || ""}`;
    const groups = {};
    (stmt.invoices || []).forEach((inv) => {
      const c = (inv.currency || cur).toUpperCase();
      (groups[c] = groups[c] || { total: 0, paid: 0, invoices: [] });
      groups[c].invoices.push(inv);
      groups[c].total += (inv.total || 0);
      groups[c].paid += (inv.paid_amount || 0);
    });
    const keys = Object.keys(groups).sort((a, b) => (a === cur ? -1 : b === cur ? 1 : a.localeCompare(b)));
    const typeLabel = (i) => (i.kind === "sale" ? "بيع" : i.kind === "purchase" ? "شراء" : (i.kind === "sale_return" || i.kind === "purchase_return") ? "مرتجع" : i.kind);
    const invBlocks = keys.map((k) => {
      const rows = groups[k].invoices.map((i) => {
        const isRet = i.kind === "sale_return" || i.kind === "purchase_return";
        const products = showProducts && (i.lines || []).length ? `<tr><td colspan="5" style="background:#f8fafc"><b>تفنيد المنتجات:</b><br/>${i.lines.map(l => `${l.product_name || "صنف"} — ${l.quantity || 0} ${l.unit_name || ""}`).join("<br/>")}</td></tr>` : "";
        return `<tr><td>#${i.number}</td><td>${typeLabel(i)}</td><td>${new Date(i.created_at).toLocaleDateString("ar-EG")}</td><td style="color:${isRet ? "#b45309" : "inherit"}">${isRet ? "- " : ""}${money(i.total, i.currency)}</td><td>${i.payment_type === "cash" ? "نقدي" : "آجل"}</td></tr>${products}`;
      }).join("");
      return `<div class="grp"><div class="grphd"><b>${k}</b><span>الصافي: ${money(groups[k].total - groups[k].paid, k)}</span></div><table><thead><tr><th>رقم</th><th>النوع</th><th>التاريخ</th><th>الإجمالي</th><th>الدفع</th></tr></thead><tbody>${rows}</tbody></table></div>`;
    }).join("");
    const vRows = (stmt.vouchers || []).map((v) => `<tr><td>#${v.number}</td><td>${new Date(v.created_at).toLocaleDateString("ar-EG")}</td><td>${money(v.amount, v.currency)}</td><td>${({ cash: "نقدي", bank: "حوالة بنكية", cheque: "شيك" })[v.method] || v.method}</td><td>${v.notes || ""}</td></tr>`).join("");
    const vBlock = (stmt.vouchers || []).length ? `<h2>${kind === "customer" ? "سندات القبض" : "سندات الدفع"}</h2><table><thead><tr><th>رقم</th><th>التاريخ</th><th>المبلغ</th><th>الطريقة</th><th>ملاحظات</th></tr></thead><tbody>${vRows}</tbody></table>` : "";
    const html = `<!doctype html><html dir="rtl"><head><meta charset="utf-8"><title>كشف حساب ${p.name || ""}</title>
      <style>
        @page { size:A4; margin:12mm; }
        body{ font-family:Arial,"Segoe UI",sans-serif; color:#1e293b; }
        h1{ font-size:20px; border-bottom:3px solid #2563eb; padding-bottom:6px; margin:0 0 4px }
        h2{ font-size:14px; margin:16px 0 6px; color:#334155 }
        .meta{ display:flex; justify-content:space-between; flex-wrap:wrap; gap:6px; font-size:12px; color:#475569; margin:6px 0 }
        .summary{ display:grid; grid-template-columns:repeat(3,1fr); gap:10px; margin:12px 0 }
        .kpi{ border:1px solid #e5e7eb; border-radius:8px; padding:8px; text-align:center }
        .kpi .lbl{ font-size:11px; color:#64748b } .kpi .val{ font-size:16px; font-weight:bold; margin-top:3px; font-variant-numeric:tabular-nums }
        table{ width:100%; border-collapse:collapse; font-size:12px; margin-bottom:6px }
        th{ background:#f1f5f9; padding:6px; text-align:right; color:#334155 }
        td{ padding:6px; border-bottom:1px solid #e5e7eb; text-align:right; font-variant-numeric:tabular-nums }
        .grp{ margin:10px 0 } .grphd{ display:flex; justify-content:space-between; background:#eff6ff; padding:5px 8px; border-radius:6px; font-size:12px; color:#1d4ed8; margin-bottom:4px }
        .footer{ margin-top:18px; text-align:center; color:#94a3b8; font-size:10px }
      </style></head><body>
      <h1>كشف حساب — ${p.name || ""}</h1>
      <div class="meta"><div>${company}</div><div>${p.phone ? "هاتف: " + p.phone : ""}</div><div>تاريخ الطباعة: ${new Date().toLocaleString("ar-EG")}</div></div>
      <div class="summary">
        <div class="kpi"><div class="lbl">رصيد افتتاحي</div><div class="val">${money(p.opening_balance || 0, cur)}</div></div>
        <div class="kpi"><div class="lbl">الرصيد الحالي</div><div class="val">${money(p.balance || 0, cur)}</div></div>
        <div class="kpi"><div class="lbl">عدد الفواتير</div><div class="val">${(stmt.invoices || []).length}</div></div>
      </div>
      <h2>الفواتير</h2>
      ${invBlocks || '<div style="text-align:center;color:#94a3b8;padding:12px">لا توجد فواتير</div>'}
      ${vBlock}
      <div class="footer">Muhasabaty — كشف حساب ${kind === "customer" ? "عميل" : "مورد"}</div>
      </body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    const idoc = iframe.contentWindow.document;
    idoc.open(); idoc.write(html); idoc.close();
    setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {} setTimeout(() => iframe.remove(), 1500); }, 350);
  };

  return (
    <div className="space-y-6" data-testid={`${kind}-page`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{kind==="customer"?t("party_sub_customer"):t("party_sub_supplier")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} data-testid={`add-${kind}-button`}><Plus className="w-4 h-4 me-1" /> {t("add")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing?t("edit"):t("add")} {partyWord}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("name")}</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="party-name-field" /></div>
              <div><Label>{t("phone")}</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} /></div>
              <div><Label>{t("party_address")}</Label><Input value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})} /></div>
              <div><Label>{t("party_opening_currency")}</Label><select className="w-full h-10 border rounded bg-background px-2" value={form.currency || cur} onChange={(e)=>setForm({...form, currency:e.target.value})}>{CURRENCIES.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
              <div><Label>{t("party_opening_balance")} ({form.currency || cur})</Label><Input type="number" value={form.opening_balance} onChange={(e)=>setForm({...form, opening_balance:e.target.value})} /></div>
              <div><Label>{t("notes")}</Label><Input value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} /></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid={`save-${kind}-button`}>{t("save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-start">{t("name")}</TableHead>
            <TableHead className="text-start">{t("phone")}</TableHead>
            <TableHead className="text-start">{t("party_balances_by_currency")}</TableHead>
            <TableHead className="text-start">{t("actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">{t("no_records")}</TableCell></TableRow>}
            {items.map(p => (
              <TableRow key={p.id}>
                <TableCell className="font-semibold"><button type="button" className="text-primary hover:underline text-start" onClick={()=>showStatement(p)} data-testid={`open-party-${p.id}`}>{p.name}</button></TableCell>
                <TableCell>{p.phone||"—"}</TableCell>
                <TableCell className="tabular-nums font-bold"><div className="flex flex-wrap gap-1">{Object.entries(p.balances || {[p.currency || cur]: p.balance || 0}).filter(([,v]) => Number(v) !== 0).map(([c,v]) => <span key={c} className={`px-1.5 py-0.5 rounded bg-muted ${Number(v)>0?"text-destructive":"text-emerald-600"}`}>{fmtMoney(Number(v), c)}</span>)}{(!p.balances || Object.keys(p.balances).length === 0) && <span>{fmtMoney(p.balance||0, p.currency || cur)}</span>}</div></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={()=>showStatement(p)} title={t("party_statement")}><FileText className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={()=>emailStatement(p)} title={t("party_email_stmt")} className="text-blue-600" data-testid={`email-stmt-${p.id}`}><Mail className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={()=>whatsappStatement(p)} title={t("party_wa_stmt")} className="text-green-600" data-testid={`wa-stmt-${p.id}`}><MessageCircle className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={()=>openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={()=>del(p.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!stmt} onOpenChange={(o)=>!o && setStmt(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              {t("party_statement")} — {stmt?.party?.name}
              <Button size="sm" variant={showProducts ? "default" : "outline"} onClick={() => setShowProducts(v => !v)} disabled={!stmt} className="gap-1" data-testid="toggle-statement-products"><PackageSearch className="w-3.5 h-3.5" /> {showProducts ? "إخفاء المنتجات" : "تفنيد المنتجات"}</Button>
              <Button size="sm" variant="outline" onClick={printStatement} disabled={!stmt} className="ms-auto gap-1" data-testid="print-party-stmt-btn"><Printer className="w-3.5 h-3.5" /> طباعة PDF</Button>
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm">
            <div className="grid grid-cols-3 gap-3 border border-border rounded-md p-3 mb-3 bg-muted/40">
              <div><div className="text-xs text-muted-foreground">{t("party_opening_balance")}</div><div className="font-bold tabular-nums">{fmtMoney(stmt?.party?.opening_balance||0, cur)}</div></div>
              <div><div className="text-xs text-muted-foreground">{t("party_current_balance")}</div><div className="font-bold tabular-nums">{fmtMoney(stmt?.party?.balance||0, cur)}</div></div>
              <div><div className="text-xs text-muted-foreground">{t("party_invoices_count")}</div><div className="font-bold tabular-nums">{stmt?.invoices?.length||0}</div></div>
            </div>
            {(() => {
              // Group by currency
              const groups = {};
              (stmt?.invoices || []).forEach(inv => {
                const c = (inv.currency || cur).toUpperCase();
                if (!groups[c]) groups[c] = { total: 0, paid: 0, invoices: [] };
                groups[c].invoices.push(inv);
                groups[c].total += (inv.total || 0);
                groups[c].paid += (inv.paid_amount || 0);
              });
              const keys = Object.keys(groups).sort((a,b)=> a===cur?-1:b===cur?1:a.localeCompare(b));
              if (keys.length === 0) return <div className="text-center text-muted-foreground py-6">{t("party_no_invoices")}</div>;
              return keys.map(k => (
                <div key={k} className="mb-4" data-testid={`stmt-section-${k}`}>
                  <div className="flex items-center justify-between mb-1 px-2 py-1 bg-blue-50 dark:bg-blue-950/20 rounded">
                    <div className="text-xs font-semibold text-blue-700 dark:text-blue-300">{ti("party_section", { k })}</div>
                    <div className="text-xs text-muted-foreground">{t("party_net")} <b className="tabular-nums text-foreground">{fmtMoney(groups[k].total - groups[k].paid, k)}</b></div>
                  </div>
                  <div className="max-h-52 overflow-auto border border-border rounded-md">
                    <Table>
                      <TableHeader><TableRow><TableHead>{t("col_number")}</TableHead><TableHead>{t("type")}</TableHead><TableHead>{t("date")}</TableHead><TableHead>{t("total")}</TableHead><TableHead>{t("party_payment")}</TableHead><TableHead>{t("party_return_reason")}</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {groups[k].invoices.map(i => {
                          const isRet = i.kind === "sale_return" || i.kind === "purchase_return";
                          const reasonMap = {damaged:t("party_reason_damaged"),expired:t("party_reason_expired"),not_satisfied:t("party_reason_not_satisfied"),wrong_item:t("party_reason_wrong_item"),other:t("party_reason_other")};
                          return (<React.Fragment key={i.id}>
                            <TableRow className={`cursor-pointer hover:bg-muted/60 ${isRet ? "bg-amber-50/50 dark:bg-amber-950/10" : ""}`} onClick={()=>goToInvoice(i)} title="فتح الفاتورة" data-testid={`stmt-invoice-${i.id}`}>
                              <TableCell className="font-mono">#{i.number}</TableCell>
                              <TableCell>{isRet ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">{t("party_return")}</span> : <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">{i.kind === "sale" ? t("party_sale") : t("party_purchase")}</span>}</TableCell>
                              <TableCell className="text-xs">{new Date(i.created_at).toLocaleDateString("ar-EG")}</TableCell>
                              <TableCell className={`tabular-nums ${isRet ? "text-amber-700 font-bold" : ""}`}>{isRet ? "- " : ""}{fmtMoney(i.total, i.currency)}</TableCell>
                              <TableCell className="text-xs">{i.payment_type==="cash"?t("cash"):t("credit")}</TableCell>
                              <TableCell className="text-xs">{isRet && i.return_reason ? <span className="text-amber-700">{reasonMap[i.return_reason] || i.return_reason}</span> : "—"}</TableCell>
                            </TableRow>
                            {showProducts && (i.lines || []).length > 0 && <TableRow className="bg-slate-50/80 dark:bg-slate-900/30"><TableCell colSpan={6} className="py-2"><div className="text-xs font-semibold text-slate-600 mb-1">تفنيد منتجات الفاتورة #{i.number}</div><div className="grid grid-cols-1 md:grid-cols-2 gap-1">{i.lines.map((l, li) => <div key={li} className="flex justify-between border rounded px-2 py-1 bg-background text-xs"><span>{l.product_name || "صنف"} {l.unit_name ? `(${l.unit_name})` : ""}</span><span className="font-bold tabular-nums">{l.quantity || 0} × {fmtMoney(l.price || 0, i.currency)}</span></div>)}</div></TableCell></TableRow>}
                          </React.Fragment>);
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ));
            })()}
            {stmt?.vouchers?.length > 0 && (
              <div className="mt-2" data-testid="stmt-vouchers">
                <div className="px-2 py-1 bg-emerald-50 dark:bg-emerald-950/20 rounded text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-1">
                  {kind === "customer" ? "سندات القبض" : "سندات الدفع"} ({stmt.vouchers.length})
                </div>
                <div className="max-h-40 overflow-auto border border-border rounded-md">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>رقم</TableHead><TableHead>التاريخ</TableHead><TableHead>المبلغ</TableHead><TableHead>الطريقة</TableHead><TableHead>ملاحظات</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {stmt.vouchers.map(v => (
                        <TableRow key={v.id} data-testid={`voucher-row-${v.id}`}>
                          <TableCell className="font-mono">#{v.number}</TableCell>
                          <TableCell className="text-xs">{new Date(v.created_at).toLocaleDateString("ar-EG")}</TableCell>
                          <TableCell className="tabular-nums font-bold text-emerald-700">{fmtMoney(v.amount, v.currency)}</TableCell>
                          <TableCell className="text-xs">{({cash:"نقدي",bank:"حوالة بنكية",cheque:"شيك"})[v.method] || v.method}</TableCell>
                          <TableCell className="text-xs truncate max-w-[160px]">{v.notes || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
